"use client";

import { createPortal } from "react-dom";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent } from "react";
import Link from "next/link";
import { SITE_CONFIG } from "@/lib/constants";
import Logo from "@/components/vectors/Logo";

type LogoMenuPoint = { x: number; y: number };

/** Clipboard markup: explicit fill so pasted SVG works without Tailwind. */
function getLogoSvgClipboardMarkup(svg: SVGSVGElement): string {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute("fill", "#111111");
  const raw = clone.getAttribute("class");
  if (raw) {
    const next = raw
      .split(/\s+/)
      .filter(Boolean)
      .filter((c) => !/^fill-/.test(c) && !/:fill-/.test(c))
      .join(" ");
    if (next) clone.setAttribute("class", next);
    else clone.removeAttribute("class");
  }
  return clone.outerHTML;
}

const LOGO_MENU_EST_W = 220;
const LOGO_MENU_EST_H = 112;

function clampLogoMenuPoint(x: number, y: number): LogoMenuPoint {
  if (typeof window === "undefined") return { x, y };
  const margin = 8;
  const maxX = Math.max(margin, window.innerWidth - LOGO_MENU_EST_W - margin);
  const maxY = Math.max(margin, window.innerHeight - LOGO_MENU_EST_H - margin);
  return {
    x: Math.min(Math.max(margin, x), maxX),
    y: Math.min(Math.max(margin, y), maxY),
  };
}

export function LogoNavMenu() {
  const logoSvgRef = useRef<SVGSVGElement>(null);
  const logoLinkRef = useRef<HTMLAnchorElement>(null);
  const logoMenuRef = useRef<HTMLDivElement>(null);
  const [copyAnnounce, setCopyAnnounce] = useState("");
  const [logoMenu, setLogoMenu] = useState<LogoMenuPoint | null>(null);
  const logoMenuId = useId();

  const closeLogoMenu = useCallback(() => {
    setLogoMenu(null);
  }, []);

  const openLogoMenuAt = useCallback((x: number, y: number) => {
    setLogoMenu(clampLogoMenuPoint(x, y));
  }, []);

  const onLogoContextMenu = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      openLogoMenuAt(e.clientX, e.clientY);
    },
    [openLogoMenuAt],
  );

  const onLogoKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLAnchorElement>) => {
      if (e.shiftKey && e.key === "F10") {
        e.preventDefault();
        const el = logoLinkRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        openLogoMenuAt(r.left, r.bottom + 6);
      }
    },
    [openLogoMenuAt],
  );

  const copyLogoSvg = useCallback(async () => {
    closeLogoMenu();
    const svg = logoSvgRef.current;
    if (!svg) return;
    try {
      await navigator.clipboard.writeText(getLogoSvgClipboardMarkup(svg));
      setCopyAnnounce("Logo SVG copied to clipboard");
    } catch {
      setCopyAnnounce("Could not copy SVG");
    }
    window.setTimeout(() => setCopyAnnounce(""), 2500);
    logoLinkRef.current?.focus();
  }, [closeLogoMenu]);

  const openLlmsTxt = useCallback(() => {
    closeLogoMenu();
    window.open("/llms.txt", "_blank", "noopener,noreferrer");
    logoLinkRef.current?.focus();
  }, [closeLogoMenu]);

  useEffect(() => {
    if (!logoMenu) return;
    const onDocMouseDown = (e: globalThis.MouseEvent) => {
      if (!(e.target instanceof Node)) return;
      if (logoMenuRef.current?.contains(e.target)) return;
      closeLogoMenu();
    };
    const onEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") closeLogoMenu();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onEscape);
    };
  }, [logoMenu, closeLogoMenu]);

  useLayoutEffect(() => {
    if (!logoMenu) return;
    logoMenuRef.current
      ?.querySelector<HTMLButtonElement>("[data-logo-menu-item]")
      ?.focus();
  }, [logoMenu]);

  return (
    <>
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {copyAnnounce}
      </span>
      <Link
        ref={logoLinkRef}
        href="/"
        className="text-text-light transition-opacity hover:opacity-80 dark:text-text-dark"
        aria-label={`${SITE_CONFIG.name} - Home`}
        aria-haspopup="menu"
        aria-expanded={logoMenu != null}
        aria-controls={logoMenu != null ? logoMenuId : undefined}
        title="Right-click or Shift+F10 for logo options"
        onContextMenu={onLogoContextMenu}
        onKeyDown={onLogoKeyDown}
      >
        <Logo
          ref={logoSvgRef}
          className="h-6 w-auto fill-black dark:fill-white"
        />
      </Link>
      {logoMenu != null &&
        createPortal(
          <div
            ref={logoMenuRef}
            id={logoMenuId}
            role="menu"
            aria-label="Logo options"
            className="fixed z-[220] min-w-[11rem] overflow-hidden rounded-lg border border-black/10 bg-tan py-1 text-sm shadow-[0_16px_48px_rgba(0,0,0,0.18)] dark:border-white/12 dark:bg-background-dark dark:shadow-[0_16px_48px_rgba(0,0,0,0.45)]"
            style={{ left: logoMenu.x, top: logoMenu.y }}
          >
            <button
              type="button"
              role="menuitem"
              data-logo-menu-item
              className="flex w-full cursor-pointer px-3 py-2.5 text-left text-text-light hover:bg-black/[0.06] focus:bg-black/[0.06] focus:outline-none dark:text-text-dark dark:hover:bg-white/10 dark:focus:bg-white/10"
              onClick={() => void copyLogoSvg()}
            >
              Copy logo as SVG
            </button>
            <button
              type="button"
              role="menuitem"
              className="flex w-full cursor-pointer px-3 py-2.5 text-left text-text-light hover:bg-black/[0.06] focus:bg-black/[0.06] focus:outline-none dark:text-text-dark dark:hover:bg-white/10 dark:focus:bg-white/10"
              onClick={openLlmsTxt}
            >
              Open site summary (llms.txt)
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}
